import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Bell, AlertCircle, Clock, Sparkles, Check } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { getExpiryStatus } from "./ExpiryBanner";

const reminderTitle = (status, daysLeft) => {
  if (status === "expired") return "Gallery expired";
  if (status === "critical") return "Less than 24 hours left";
  return `${daysLeft} day${daysLeft === 1 ? "" : "s"} left`;
};

const NotificationItem = ({ event, daysLeft, status, onDismiss, onNavigate }) => {
  const Icon = status === "warning" ? Clock : AlertCircle;
  const tone = status === "warning" ? "text-amber-600 bg-amber-50" : "text-red-600 bg-red-50";
  return (
    <div className="px-4 py-3 border-b border-cream-200 last:border-0 hover:bg-cream-50">
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-full ${tone} flex items-center justify-center shrink-0`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-navy-700 text-sm truncate">{event.name}</div>
          <div className="text-xs text-navy-700/70 mt-0.5">
            {reminderTitle(status, daysLeft)} · {event.tier === "free" ? "Free Demo" : event.tier} tier
          </div>
          <div className="mt-2 flex items-center gap-2">
            <Link
              to={`/dashboard/events/${event.id}/upgrade`}
              onClick={onNavigate}
              className="bg-coral-300 text-white px-3 py-1.5 rounded-md text-xs font-semibold hover:bg-coral-400 inline-flex items-center gap-1"
            >
              <Sparkles className="w-3 h-3" /> Upgrade
            </Link>
            <button onClick={() => onDismiss(event.id)} className="text-xs text-navy-700/60 hover:text-navy-700">
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const NotificationPanel = ({ visible, onDismiss, onDismissAll, onNavigate }) => (
  <div className="absolute right-0 top-full mt-2 w-[340px] bg-white rounded-2xl shadow-xl border border-cream-200 z-50 overflow-hidden">
    <div className="px-4 py-3 border-b border-cream-200 flex items-center justify-between">
      <div>
        <div className="font-semibold text-navy-700 text-sm">Reminders</div>
        <div className="text-xs text-navy-700/60">{visible.length} event{visible.length === 1 ? "" : "s"} need attention</div>
      </div>
      {visible.length > 0 && (
        <button onClick={onDismissAll} className="text-xs text-coral-300 font-semibold hover:text-coral-400 inline-flex items-center gap-1">
          <Check className="w-3 h-3" /> Mark all read
        </button>
      )}
    </div>
    <div className="max-h-[360px] overflow-y-auto">
      {visible.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm text-navy-700/60">
          <Check className="w-6 h-6 mx-auto mb-2 text-emerald-400" />
          You&apos;re all caught up.
        </div>
      ) : (
        visible.map((r) => (
          <NotificationItem key={r.event.id} {...r} onDismiss={onDismiss} onNavigate={onNavigate} />
        ))
      )}
    </div>
  </div>
);

const useReminders = (user) => {
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/events");
      const reminders = res.data
        .map(e => ({ event: e, ...getExpiryStatus(e.expires_at, e.tier) }))
        .filter(r => r.status !== "safe");
      setItems(reminders);
    } catch (error) {
      console.error("Failed to load reminders:", error);
    }
  }, []);

  useEffect(() => {
    if (!user) { setItems([]); return; }
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [user, load]);

  return items;
};

const NotificationBell = () => {
  const { user } = useAuth() || {};
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("gp_dismissed_reminders") || "[]"); } catch { return []; }
  });
  const wrap = useRef(null);
  const items = useReminders(user);

  useEffect(() => {
    const onClick = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const persistDismissed = (next) => {
    setDismissed(next);
    localStorage.setItem("gp_dismissed_reminders", JSON.stringify(next));
  };

  const dismiss = (eventId) => persistDismissed([...new Set([...dismissed, eventId])]);
  const dismissAll = () => persistDismissed([...new Set([...dismissed, ...items.map(i => i.event.id)])]);

  const visible = items.filter(i => !dismissed.includes(i.event.id));
  if (!user) return null;

  return (
    <div className="relative" ref={wrap}>
      <button onClick={() => setOpen(!open)} className="relative w-9 h-9 rounded-full bg-cream-200/70 text-navy-700 flex items-center justify-center hover:bg-cream-200 transition-colors" aria-label="Notifications">
        <Bell className="w-4 h-4" />
        {visible.length > 0 && (
          <span className="absolute -top-1 -right-1 bg-coral-300 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center ring-2 ring-cream-100">
            {visible.length}
          </span>
        )}
      </button>
      {open && (
        <NotificationPanel
          visible={visible}
          onDismiss={dismiss}
          onDismissAll={dismissAll}
          onNavigate={() => setOpen(false)}
        />
      )}
    </div>
  );
};

export default NotificationBell;
